import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Pager for keyset (cursor) endpoints.
 *
 * There is no page count and there never will be — that is the point of cursor
 * paging. The backend stopped issuing `COUNT(*)` over an append-only table that
 * grows forever, so the UI must not imply a total it cannot know. Where a total
 * IS meaningful it comes from the summary endpoint and is shown on a card, not
 * inferred from pagination.
 *
 * "Back" walks a cursor stack the caller keeps. Cursors are opaque and only
 * point forward, so the previous page is remembered, not computed.
 */
export function CursorPager({
  page, count, hasMore, canGoBack, onBack, onNext, loading, unit = 'rows',
}: {
  /** Zero-based index of the page being shown, for the "Page N" label. */
  page: number
  count: number
  hasMore: boolean
  canGoBack: boolean
  onBack: () => void
  onNext: () => void
  loading?: boolean
  unit?: string
}) {
  return (
    <div className="px-4 py-2 border-t border-border flex items-center justify-between gap-3 bg-muted/20">
      <div className="text-erp-xs text-muted-foreground min-w-0 truncate">
        {loading
          ? <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</span>
          : <>Page {page + 1} · {count} {unit}{hasMore ? '' : ' · end of results'}</>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          type="button"
          className={cn('erp-btn', !canGoBack && 'opacity-40 cursor-not-allowed')}
          onClick={onBack}
          disabled={!canGoBack || loading}
         aria-label="Back">
          <ChevronLeft className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Back</span>
        </button>
        <button
          type="button"
          className={cn('erp-btn', !hasMore && 'opacity-40 cursor-not-allowed')}
          onClick={onNext}
          disabled={!hasMore || loading}
          aria-label="Next"
        >
          <span className="axis-hide-sm">Next</span> <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

/** Offset pager, for the endpoints that still page by page number. */
export function OffsetPager({
  page, totalPages, totalElements, onPage, loading, unit = 'rows',
}: {
  page: number
  totalPages: number
  totalElements: number
  onPage: (p: number) => void
  loading?: boolean
  unit?: string
}) {
  const last = Math.max(0, totalPages - 1)
  return (
    <div className="px-4 py-2 border-t border-border flex items-center justify-between gap-3 bg-muted/20">
      <div className="text-erp-xs text-muted-foreground min-w-0 truncate">
        {loading
          ? <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</span>
          : <>Page {page + 1} of {Math.max(1, totalPages)} · {totalElements} {unit}</>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          type="button"
          className={cn('erp-btn', page === 0 && 'opacity-40 cursor-not-allowed')}
          onClick={() => onPage(Math.max(0, page - 1))}
          disabled={page === 0 || loading}
         aria-label="Back">
          <ChevronLeft className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Back</span>
        </button>
        <button
          type="button"
          className={cn('erp-btn', page >= last && 'opacity-40 cursor-not-allowed')}
          onClick={() => onPage(Math.min(last, page + 1))}
          disabled={page >= last || loading}
         aria-label="Next">
          <span className="axis-hide-sm">Next</span> <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
